import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { PlusCircle, Sparkles, Edit2, Trash2, Save, X, Target, Home, GraduationCap, Rocket, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const GOAL_TYPES = [
  { value: 'retirement', label: 'Retirement', icon: Rocket },
  { value: 'home', label: 'Home', icon: Home },
  { value: 'education', label: 'Education', icon: GraduationCap },
  { value: 'custom', label: 'Custom', icon: Target },
];

const emptyGoal = {
  name: '',
  type: 'custom',
  target_amount: 0,
  target_date: new Date(new Date().getFullYear() + 5, 0, 1).toISOString().slice(0, 10),
  monthly_contribution: 0,
  manual_current_amount: null,
  linked_account_ids: [],
  notes: '',
};

const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n) || 0);
const iconFor = (type) => (GOAL_TYPES.find((t) => t.value === type) || GOAL_TYPES[3]).icon;

const WealthGoals = () => {
  const [goals, setGoals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyGoal);
  const [editingId, setEditingId] = useState(null);
  const [aiPlans, setAiPlans] = useState({}); // goalId -> plan
  const [aiLoading, setAiLoading] = useState({});

  const load = useCallback(async () => {
    try {
      const [g, a] = await Promise.all([
        axios.get(`${API}/goals`),
        axios.get(`${API}/wealth/accounts`),
      ]);
      setGoals(g.data);
      setAccounts(a.data);
    } catch (e) {
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLinkAccount = (accountId) => {
    const linked = draft.linked_account_ids || [];
    if (linked.includes(accountId)) {
      setDraft({ ...draft, linked_account_ids: linked.filter((x) => x !== accountId) });
    } else {
      setDraft({ ...draft, linked_account_ids: [...linked, accountId] });
    }
  };

  const save = async () => {
    if (!draft.name || draft.target_amount <= 0) {
      toast.error('Name and positive target amount required.');
      return;
    }
    try {
      const payload = { ...draft };
      if (payload.manual_current_amount === '' || payload.manual_current_amount == null) delete payload.manual_current_amount;
      if (editingId) {
        await axios.put(`${API}/goals/${editingId}`, payload);
        toast.success('Goal updated');
      } else {
        await axios.post(`${API}/goals`, payload);
        toast.success('Goal added');
      }
      setAdding(false);
      setEditingId(null);
      setDraft(emptyGoal);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const startEdit = (g) => {
    setEditingId(g.id);
    setAdding(true);
    setDraft({
      name: g.name,
      type: g.type,
      target_amount: g.target_amount,
      target_date: g.target_date,
      monthly_contribution: g.monthly_contribution,
      manual_current_amount: g.manual_current_amount,
      linked_account_ids: g.linked_account_ids || [],
      notes: g.notes || '',
    });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/goals/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const askAi = async (id) => {
    setAiLoading((p) => ({ ...p, [id]: true }));
    try {
      const r = await axios.post(`${API}/goals/${id}/ai-plan`);
      setAiPlans((p) => ({ ...p, [id]: r.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI plan failed');
    } finally {
      setAiLoading((p) => ({ ...p, [id]: false }));
    }
  };

  if (loading) {
    return <div className="p-8 md:p-12 font-chivo text-xl text-slate-900" data-testid="goals-loading">Loading goals…</div>;
  }

  return (
    <div className="p-8 md:p-12" data-testid="wealth-goals-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">Goals</h1>
            <p className="text-slate-600 mt-1 font-ibm">Set targets for retirement, home, education — see what it takes to hit them.</p>
          </div>
          {!adding && (
            <Button onClick={() => { setAdding(true); setDraft(emptyGoal); setEditingId(null); }}
              className="bg-slate-900 text-white rounded-sm hover:bg-slate-800" data-testid="new-goal-btn">
              <PlusCircle size={14} className="mr-2" />New goal
            </Button>
          )}
        </div>

        {adding && (
          <div className="mb-6 border border-slate-200 bg-slate-50 p-6 rounded-sm" data-testid="goal-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="Goal name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="goal-name" />
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger data-testid="goal-type"><SelectValue /></SelectTrigger>
                <SelectContent>{GOAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Target amount" value={draft.target_amount} onChange={(e) => setDraft({ ...draft, target_amount: parseFloat(e.target.value) || 0 })} data-testid="goal-target" />
              <Input type="date" value={draft.target_date} onChange={(e) => setDraft({ ...draft, target_date: e.target.value })} data-testid="goal-date" />
              <Input type="number" placeholder="Monthly contribution" value={draft.monthly_contribution} onChange={(e) => setDraft({ ...draft, monthly_contribution: parseFloat(e.target.value) || 0 })} data-testid="goal-contribution" />
              <Input type="number" placeholder="Manual current (optional)" value={draft.manual_current_amount ?? ''} onChange={(e) => setDraft({ ...draft, manual_current_amount: e.target.value === '' ? null : parseFloat(e.target.value) })} data-testid="goal-manual-current" />
            </div>
            {accounts.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600 mb-2">Link accounts (auto-progress)</p>
                <div className="flex flex-wrap gap-2">
                  {accounts.map((a) => {
                    const linked = draft.linked_account_ids?.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleLinkAccount(a.id)}
                        className={`px-3 py-1.5 text-xs font-ibm rounded-sm border ${linked ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-700'}`}
                        data-testid={`link-account-${a.id}`}
                      >
                        {a.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={save} className="bg-slate-900 text-white rounded-sm" data-testid="save-goal-btn">
                <Save size={14} className="mr-2" />{editingId ? 'Update' : 'Save'}
              </Button>
              <Button variant="outline" onClick={() => { setAdding(false); setEditingId(null); setDraft(emptyGoal); }} className="rounded-sm" data-testid="cancel-goal-btn">
                <X size={14} className="mr-2" />Cancel
              </Button>
            </div>
          </div>
        )}

        {goals.length === 0 && !adding ? (
          <div className="border border-dashed border-slate-300 rounded-sm p-12 text-center" data-testid="no-goals">
            <Target size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="font-ibm text-slate-600">No goals yet. Add one above to see what it takes to hit it.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {goals.map((g) => {
              const Icon = iconFor(g.type);
              const plan = aiPlans[g.id];
              return (
                <div key={g.id} className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`goal-card-${g.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                        <Icon size={18} />
                      </div>
                      <div>
                        <h3 className="font-chivo font-bold text-slate-900 text-lg">{g.name}</h3>
                        <p className="text-xs font-ibm text-slate-500 uppercase tracking-widest mt-0.5">
                          {g.type} · <Calendar size={10} className="inline" /> {g.target_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(g)} data-testid={`edit-goal-${g.id}`}><Edit2 size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => del(g.id)} data-testid={`delete-goal-${g.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                    </div>
                  </div>

                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-sm font-ibm text-slate-600">Progress</span>
                    <span className="text-sm font-chivo font-bold text-slate-900 tabular-nums" data-testid={`goal-progress-${g.id}`}>
                      {fmt(g.current_amount)} / {fmt(g.target_amount)}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-sm overflow-hidden mb-2">
                    <div
                      className={`h-full ${g.on_track ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min(100, g.progress_pct)}%` }}
                      data-testid={`goal-progress-bar-${g.id}`}
                    />
                  </div>
                  <div className="flex justify-between text-xs font-ibm text-slate-600">
                    <span>{g.progress_pct.toFixed(1)}% complete</span>
                    <span>{g.months_remaining} months left</span>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-sm font-ibm">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-widest">You contribute</p>
                      <p className="font-chivo font-bold text-slate-900 tabular-nums mt-1">{fmt(g.monthly_contribution)}<span className="text-xs text-slate-500 font-ibm">/mo</span></p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-widest">Math says you need</p>
                      <p className={`font-chivo font-bold tabular-nums mt-1 ${g.on_track ? 'text-emerald-600' : 'text-amber-600'}`} data-testid={`goal-required-${g.id}`}>
                        {fmt(g.required_monthly_contribution)}<span className="text-xs text-slate-500 font-ibm">/mo</span>
                      </p>
                    </div>
                  </div>

                  <Button onClick={() => askAi(g.id)} disabled={aiLoading[g.id]} className="w-full mt-4 bg-slate-900 text-white rounded-sm hover:bg-slate-800" data-testid={`ai-plan-btn-${g.id}`}>
                    <Sparkles size={14} className="mr-2" />
                    {aiLoading[g.id] ? 'Thinking…' : 'AI savings plan'}
                  </Button>

                  {plan && (
                    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-3 bg-indigo-50 border-l-4 border-indigo-500 p-3 rounded-sm" data-testid={`ai-plan-${g.id}`}>
                      <p className="text-sm font-ibm text-slate-800">{plan.insight}</p>
                      {plan.action_items?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {plan.action_items.map((a, i) => (
                            <li key={i} className="text-xs font-ibm text-slate-700 flex gap-2">
                              <span className="text-indigo-600">→</span>
                              <span>{a}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-2 text-xs font-ibm font-semibold text-indigo-700">
                        Suggested: {fmt(plan.suggested_monthly_contribution)}/mo
                      </p>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default WealthGoals;
